import type { Unit } from '@app/src/types/resourceExplorer'
import { parseCurrencyInput, toExactCurrencyString } from '@app/src/utils/currencyUtils'
import type { FilterOption, MrtColumnConfig, MrtColumnFilterVariant } from '../mrtCollectionTypes'
import { readStoredFilterValue } from './filterOperator'

/**
 * Pure helpers behind the filter chips and the "Add filter" picker. No I/O — the shell (bar, chip,
 * add-button) gathers columns/values and delegates the display and eligibility logic here.
 *
 * Multi-select values are stored as {value,label} pairs, so the chip label comes straight from the
 * persisted value with no facet lookup.
 */

export const getFilterableColumns = <T>(columns: MrtColumnConfig<T>[]): MrtColumnConfig<T>[] =>
  columns.filter(column => column.filter)

// Tolerate legacy plain-string entries persisted before options carried their label.
const entryLabel = (entry: unknown): string => (typeof entry === 'string' ? entry : (entry as FilterOption).label)

const summariseMultiSelect = (value: unknown): string => {
  const entries = Array.isArray(value) ? value : []
  if (!entries.length) return ''
  const [first, ...rest] = entries
  return rest.length ? `${entryLabel(first)} +${rest.length}` : entryLabel(first)
}

const hasValue = (value: unknown): boolean => value !== undefined && value !== null && value !== ''

/** A [min, max] pair as chip text, formatted in the given currency when the filter has one. */
export const summariseRange = (value: unknown, currencyUnit?: Unit): string => {
  const [min, max] = Array.isArray(value) ? value : []
  const format = (raw: unknown): string => {
    if (!currencyUnit) return String(raw)
    const parsed = parseCurrencyInput(String(raw))
    return parsed === undefined ? String(raw) : toExactCurrencyString(parsed, currencyUnit)
  }
  if (hasValue(min) && hasValue(max)) return `${format(min)}–${format(max)}`
  if (hasValue(min)) return `≥ ${format(min)}`
  if (hasValue(max)) return `≤ ${format(max)}`
  return ''
}

const toIsoDate = (value: Date | string): string => {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10)
}

const summariseDateRange = (value: unknown): string => {
  const [from, to] = Array.isArray(value) ? value : []
  const fromLabel = from ? toIsoDate(from) : ''
  const toLabel = to ? toIsoDate(to) : ''
  if (fromLabel && toLabel) return `${fromLabel}–${toLabel}`
  if (fromLabel) return `≥ ${fromLabel}`
  if (toLabel) return `≤ ${toLabel}`
  return ''
}

const optionValue = (entry: unknown): string => (typeof entry === 'string' ? entry : (entry as FilterOption).value)

// Multi-select order carries no meaning (checking B then A selects the same filter as A then B), so
// compare selections as a set; range/date-range keep positional comparison ([min, max] order matters).
const canonicalValue = (variant: MrtColumnFilterVariant | undefined, value: unknown): unknown =>
  variant === 'multi-select' && Array.isArray(value)
    ? [...value].sort((left, right) => optionValue(left).localeCompare(optionValue(right)))
    : value

// True when two stored filter values are effectively the same. Lets the chip skip a refetch when it is
// opened and closed without a real change — including re-ticking the same multi-select options.
export const isSameFilterValue = <T>(column: MrtColumnConfig<T>, a: unknown, b: unknown): boolean => {
  const left = readStoredFilterValue(a)
  const right = readStoredFilterValue(b)
  if (left.operator !== right.operator) return false
  const variant = column.filter?.variant
  return JSON.stringify(canonicalValue(variant, left.value)) === JSON.stringify(canonicalValue(variant, right.value))
}

// Summarises the filter's value only (e.g. "Sweden +2"); the operator text is composed by the chip.
export const getFilterChipSummary = <T>(column: MrtColumnConfig<T>, stored: unknown): string => {
  const { value } = readStoredFilterValue(stored)
  switch (column.filter?.variant) {
    case 'multi-select':
      return summariseMultiSelect(value)
    case 'range':
      return summariseRange(value, column.filter?.currencyUnit)
    case 'date-range':
      return summariseDateRange(value)
    case 'custom':
      return column.filter?.renderSummary?.(value) ?? ''
    default:
      return ''
  }
}
